import React from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import RatingGraphic from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { isoWebPageURL } from "../../../models/nominals";
import { DiscountDisplayMode } from "../../configurator/constants";
import { ATTRIBUTE_CLASSES, ShopTileType } from "../ShopCompareTypes";
import { AttributeList } from "../elements/AttributeList";

import styles from "./ShopTileContent.module.scss";

interface IShopTileContentProps {
    value: ShopTileType;
    tilesetId: number;
}

export const ShopTileContent = (props: IShopTileContentProps) => {
    const shopTile = props.value.shop_tile;
    const attributeList = props.value.shop_tile.feel_options.map(
        (feelOption) => ({
            title: feelOption,
            class:
                (
                    Object.keys(ATTRIBUTE_CLASSES) as Array<
                        keyof typeof ATTRIBUTE_CLASSES
                    >
                ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
        }),
    );
    return (
        <div className={styles.tileContainer}>
            <div className={styles.gradient}></div>
            {shopTile && shopTile.primary_flair && (
                <div className={styles.primaryFlair}>
                    <Flair flair={shopTile.primary_flair} />
                </div>
            )}
            {shopTile && (
                <div className={styles.headerContainer}>
                    {shopTile.product_image && (
                        <Image
                            alt={t`${shopTile.title}`}
                            src={shopTile.product_image.url}
                        />
                    )}
                    <div className={styles.contentContainer}>
                        {shopTile.secondary_flair && (
                            <div className={styles.secondaryFlair}>
                                <Flair flair={shopTile.secondary_flair} />
                            </div>
                        )}
                        <RichText
                            html={shopTile?.title || ""}
                            className={styles.title}
                        />
                        <Link
                            href={isoWebPageURL.wrap(
                                `${shopTile.link}#reviews_body`,
                            )}
                            className={styles.starRating}
                        >
                            <RatingGraphic
                                cardClass={styles.starRating}
                                cardSize="large"
                                numReviews={shopTile.num_reviews}
                                rating={shopTile.rating}
                                reviewNumberClass={styles.reviewLink}
                            />
                        </Link>
                        <RichText
                            html={shopTile?.description || ""}
                            className={styles.description}
                        />
                        <AttributeList list={attributeList} />
                        <div className={styles.priceContainer}>
                            <div>
                                <ConfiguratorProductPrice
                                    price={shopTile.price}
                                    strikeThroughMSRP={false}
                                    discountDisplayMode={
                                        DiscountDisplayMode.SHOW_ABSOLUTE
                                    }
                                    actualPriceStyle={"shop"}
                                ></ConfiguratorProductPrice>
                                <div className={styles.financingPlanContainer}>
                                    {shopTile.price.per_month_term_length && (
                                        <span>
                                            or {shopTile.price.per_month} for{" "}
                                            <span>
                                                {shopTile.price.per_month_term_length.toString()}{" "}
                                            </span>
                                        </span>
                                    )}
                                    <br />
                                    <span>
                                        (total payments of $
                                        {shopTile.price.retail})
                                    </span>
                                </div>
                            </div>
                            <div className={styles.buttons}>
                                <Link
                                    href={shopTile.link}
                                    className="button al-comparisongrid-shopnow"
                                >
                                    {t`Shop Now`}
                                    <span className="ada-screenreader-only">
                                        {interpolate(t`for %(productName)s`, {
                                            productName: shopTile.title || "",
                                        })}
                                    </span>
                                </Link>
                                <a
                                    // @ts-expect-error: reverse() expects Django route params, not always typed for FE
                                    href={`${reverse("shop-tile", { shop_tile_pk: shopTile.id })}?tileset=${props.tilesetId}`}
                                    className="button al-comparisongrid-compare"
                                >
                                    {t`Compare`}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
